/**
 * LoginPage Component
 *
 * Professional login page for Azure Entra ID authentication using Mantine UI.
 * Provides a clean, modern interface for user authentication that matches
 * the existing application design and Microsoft authentication standards.
 */

import React, { useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Loader,
  Alert,
  Center,
  Box,
  ThemeIcon
} from '@mantine/core';
import { IconLogin, IconCloud, IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../shared/contexts/AuthContext';

const LoginPage = () => {
  const { login, isLoading, authError, clearError } = useAuth();

  // Clear any existing errors when component mounts
  useEffect(() => {
    if (authError) {
      clearError();
    }
  }, [authError, clearError]);

  const handleLogin = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <Container size="sm" py={80}>
      <Center>
        <Box w="100%" maw={400}>
          <Paper shadow="md" p={30} radius="md" withBorder>
            <Stack align="center" gap="lg">
              {/* Application Header */}
              <Group gap="xs">
                <ThemeIcon size="xl" variant="light" color="blue">
                  <IconCloud size={24} />
                </ThemeIcon>
                <Box ta="center">
                  <Title order={2} size="h3" fw={600} c="blue.7">
                    Presidio Contact Center Tools
                  </Title>
                </Box>
              </Group>

              {/* Authentication Section */}
              <Stack gap="md" w="100%">

                <Text size="sm" ta="center" c="dimmed">
                  Sign in with your Presidio account to access all platform tools and features.
                </Text>

                {/* Error Display */}
                {authError && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Authentication Error"
                    color="red"
                    variant="light"
                    onClose={clearError}
                    withCloseButton
                  >
                    {authError}
                  </Alert>
                )}

                {/* Login Button */}
                <Button
                  fullWidth
                  size="md"
                  onClick={handleLogin}
                  disabled={isLoading}
                  leftSection={isLoading ? <Loader size="xs" /> : <IconLogin size={18} />}
                  variant="filled"
                  color="blue"
                >
                  {isLoading ? 'Signing In...' : 'Sign In with Microsoft'}
                </Button>

                {/* Platform Features */}
                <Stack gap="xs" mt="md">
                  <Text size="sm" fw={500} ta="center" c="gray.7">
                    Platform Features
                  </Text>
                  <Group justify="center" gap="xl">
                    <Box ta="center">
                      <Text size="xs" fw={500} c="orange.6">AWS Connect</Text>
                    </Box>
                    <Box ta="center">
                      <Text size="xs" fw={500} c="blue.6">Cisco WxCC</Text>
                    </Box>
                    <Box ta="center">
                      <Text size="xs" fw={500} c="green.6">Five9</Text>
                    </Box>
                    <Box ta="center">
                      <Text size="xs" fw={500} c="purple.6">SA Tools</Text>
                    </Box>
                  </Group>
                </Stack>
              </Stack>
            </Stack>
          </Paper>

          {/* Additional Information */}
          <Stack gap="xs" mt="lg" ta="center">
            <Text size="xs" c="dimmed">
              This application requires Azure Entra ID authentication
            </Text>
          </Stack>
        </Box>
      </Center>
    </Container>
  );
};

export default LoginPage;